import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import toast from "react-hot-toast";
import moment from "moment";
import {
  HiOutlineCollection,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineTruck,
  HiOutlineUser,
  HiOutlineOfficeBuilding,
  HiOutlineCheck,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { LogTrackingService } from "../../../../services/LogTrackingService";
import { LogService } from "../../../../services/LogService";
import DispatchBreakdown, { useDispatchBreakdown } from "./DispatchBreakdown";
import {
  LOG_TRACKING_PATH,
  BackLink,
  Field,
  inputClass,
  selectStyles,
  primaryButtonClass,
} from "../shared/ui";

const today = () => moment().format("YYYY-MM-DD");

const emptyForm = () => ({
  treeFellId: null,
  trackingDate: today(),
  waybillNumber: "",
  truckNumber: "",
  dispatchedBy: "",
  client: "",
});

const validate = (form) => {
  const errors = {};
  if (!form.treeFellId) errors.treeFellId = "Choose the log being dispatched.";
  if (!form.trackingDate) errors.trackingDate = "Enter the dispatch date.";
  if (!form.waybillNumber.trim()) errors.waybillNumber = "Enter the waybill number.";
  if (!form.truckNumber.trim()) errors.truckNumber = "Enter the truck number.";
  if (!form.dispatchedBy.trim()) errors.dispatchedBy = "Enter who dispatched the log.";
  if (!form.client.trim()) errors.client = "Enter the client.";
  return errors;
};

const LogOptionLabel = (o, { context }) =>
  context === "value" ? (
    <span className="font-mono">{o.code}</span>
  ) : (
    <span className="flex items-center min-w-0 gap-2">
      <span className="shrink-0 rounded-md bg-tclPrimary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-tclPrimary">
        {o.code}
      </span>
      <span className="truncate text-slate-500">{o.description}</span>
    </span>
  );

const LogTrackingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyForm);
  const [fells, setFells] = useState([]);
  const [fellsLoading, setFellsLoading] = useState(true);
  const [loadState, setLoadState] = useState(isEdit ? "loading" : "ready");
  const [dateCreated, setDateCreated] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    LogService.GetAllTreeLogs().then((res) => {
      setFells(
        (res || []).map((f) => ({ value: f.treeFellId, code: f.treeFellCode, description: f.description }))
      );
      setFellsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    LogTrackingService.GetLogTracking(parseInt(id)).then((res) => {
      if (!res) {
        setLoadState("error");
        return;
      }
      setForm({
        treeFellId: res.treeFellId ?? null,
        trackingDate: res.trackingDate ? moment(res.trackingDate).format("YYYY-MM-DD") : "",
        waybillNumber: res.waybillNumber ?? "",
        truckNumber: res.truckNumber ?? "",
        dispatchedBy: res.dispatchedBy ?? "",
        client: res.client ?? "",
      });
      setDateCreated(res.dateCreated);
      setLoadState("ready");
    });
  }, [id, isEdit]);

  const errors = useMemo(() => validate(form), [form]);
  const showError = (field) => (submitted || touched[field]) && errors[field];
  const selectedFell = fells.find((f) => f.value === form.treeFellId) || null;
  const breakdownIds = useMemo(() => (form.treeFellId ? [form.treeFellId] : []), [form.treeFellId]);
  const breakdown = useDispatchBreakdown(breakdownIds);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const touch = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      treeFellId: form.treeFellId,
      trackingDate: form.trackingDate,
      waybillNumber: form.waybillNumber.trim(),
      truckNumber: form.truckNumber.trim(),
      dispatchedBy: form.dispatchedBy.trim(),
      client: form.client.trim(),
    };

    setSaving(true);
    const toastId = toast.loading(isEdit ? "Saving changes…" : "Saving log tracking…");
    const request = isEdit
      ? LogTrackingService.UpdateLogTracking(parseInt(id), payload)
      : LogTrackingService.AddLogTracking(payload);

    request.then((res) => {
      toast.dismiss(toastId);
      setSaving(false);
      if (res.Successful) {
        toast.success(res.message);
        navigate(LOG_TRACKING_PATH);
      } else {
        toast.error(res.message);
      }
    });
  };

  const backLink = <BackLink to={LOG_TRACKING_PATH}>All log tracking</BackLink>;

  if (loadState !== "ready") {
    return (
      <div className="w-full pb-10 space-y-6">
        {backLink}
        <div className="p-8 bg-white border shadow-sm rounded-2xl border-slate-200/80">
          {loadState === "loading" ? (
            <div className="space-y-5 animate-pulse">
              <div className="w-48 h-6 rounded bg-slate-200" />
              <div className="h-12 rounded-xl bg-slate-100" />
              <div className="grid gap-5 md:grid-cols-2">
                <div className="h-12 rounded-xl bg-slate-100" />
                <div className="h-12 rounded-xl bg-slate-100" />
                <div className="h-12 rounded-xl bg-slate-100" />
                <div className="h-12 rounded-xl bg-slate-100" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <span className="grid mb-4 rounded-full h-14 w-14 place-items-center bg-rose-50 text-rose-500">
                <HiOutlineExclamationCircle className="h-7 w-7" />
              </span>
              <h3 className="text-base font-semibold text-slate-800">Record not found</h3>
              <p className="max-w-sm mt-1 text-sm text-slate-500">
                This tracking record could not be loaded. It may have been removed, or the connection failed.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const textField = (field, label, icon, placeholder, hint, mono) => (
    <Field id={field} label={label} icon={icon} hint={hint} error={showError(field)}>
      <input
        id={field}
        type="text"
        autoComplete="off"
        value={form[field]}
        onChange={update(field)}
        onBlur={touch(field)}
        placeholder={placeholder}
        aria-invalid={Boolean(showError(field))}
        className={`${inputClass(showError(field))}${mono ? " font-mono" : ""}`}
      />
    </Field>
  );

  return (
    <div className="w-full pb-10 space-y-6">
      {backLink}

      <header className="flex items-start gap-4">
        <span className="grid w-12 h-12 shadow-sm shrink-0 place-items-center rounded-2xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
          <HiOutlineTruck className="w-6 h-6" />
        </span>
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-800">
            {isEdit ? `Log tracking #${id}` : "Track a log"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {isEdit && dateCreated
              ? `Recorded ${moment(dateCreated).format("DD MMM YYYY [at] HH:mm")}`
              : "Record a dispatch: which log left, on which truck, and where it's going."}
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} noValidate className="grid gap-6 lg:grid-cols-3">
        <div className="bg-white border shadow-sm lg:col-span-2 rounded-2xl border-slate-200/80">
          <section className="p-6 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Log</h3>
              <p className="text-xs text-slate-500">Pick the tree-fell record being dispatched.</p>
            </div>

            <Field
              id="treeFellId"
              label="Log"
              icon={HiOutlineCollection}
              hint="Search by tree-fell code or description."
              error={showError("treeFellId")}
            >
              <ReactSelect
                inputId="treeFellId"
                isClearable
                options={fells}
                value={selectedFell}
                isLoading={fellsLoading}
                placeholder="Search logs by code or description"
                noOptionsMessage={() => (fellsLoading ? "Loading logs…" : "No logs found")}
                getOptionLabel={(o) => [o.code, o.description].filter(Boolean).join(" ")}
                formatOptionLabel={LogOptionLabel}
                onChange={(opt) => setForm((f) => ({ ...f, treeFellId: opt ? opt.value : null }))}
                onBlur={touch("treeFellId")}
                styles={selectStyles(Boolean(showError("treeFellId")))}
                aria-invalid={Boolean(showError("treeFellId"))}
              />
            </Field>

            <div className="grid gap-5 md:grid-cols-2">
              <Field id="trackingDate" label="Date" icon={HiOutlineCalendar} hint="The day the log was dispatched." error={showError("trackingDate")}>
                <input
                  id="trackingDate"
                  type="date"
                  value={form.trackingDate}
                  onChange={update("trackingDate")}
                  onBlur={touch("trackingDate")}
                  aria-invalid={Boolean(showError("trackingDate"))}
                  className={inputClass(showError("trackingDate"))}
                />
              </Field>
              {textField("waybillNumber", "Waybill No", HiOutlineDocumentText, "e.g. WB-20431", "As printed on the waybill.", true)}
            </div>
          </section>

          <section className="grid gap-5 p-6 border-t border-slate-100 md:grid-cols-2">
            <div className="md:col-span-2">
              <h3 className="text-sm font-semibold text-slate-800">Transport</h3>
              <p className="text-xs text-slate-500">Who moved the log, and who it's for.</p>
            </div>
            {textField("truckNumber", "Truck No", HiOutlineTruck, "e.g. GT 4821-22", "Vehicle registration.", true)}
            {textField("dispatchedBy", "Dispatched By", HiOutlineUser, "e.g. Kwame Mensah", "Person who released the log.")}
            <div className="md:col-span-2">
              {textField("client", "Client", HiOutlineOfficeBuilding, "e.g. Ashanti Timber Ltd", "Who the log is going to.")}
            </div>
          </section>

          <footer className="flex flex-col-reverse gap-3 px-6 py-4 border-t sm:flex-row sm:justify-end border-slate-100 bg-slate-50/60 rounded-b-2xl">
            <button
              type="button"
              onClick={() => navigate(LOG_TRACKING_PATH)}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
            >
              Cancel
            </button>
            <button type="submit" disabled={saving} className={primaryButtonClass}>
              {saving ? (
                <span className="w-4 h-4 border-2 rounded-full animate-spin border-white/40 border-t-white" />
              ) : (
                <HiOutlineCheck className="w-4 h-4" />
              )}
              {isEdit ? "Save changes" : "Save tracking"}
            </button>
          </footer>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="p-5 bg-white border shadow-sm rounded-2xl border-slate-200/80">
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-400">Summary</p>
            <dl className="mt-4 space-y-4 text-sm">
              <div>
                <dt className="text-xs text-slate-400">Log</dt>
                <dd className="mt-1">
                  {selectedFell ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-tclPrimary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-tclPrimary">
                        {selectedFell.code}
                      </span>
                      <span className="text-slate-500">{selectedFell.description}</span>
                    </span>
                  ) : (
                    <span className="text-slate-300">Not selected</span>
                  )}
                </dd>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-xs text-slate-400">Date</dt>
                  <dd className="mt-1 text-slate-800">
                    {form.trackingDate ? moment(form.trackingDate).format("DD MMM YYYY") : <span className="text-slate-300">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Waybill no</dt>
                  <dd className="mt-1 font-mono break-all text-slate-800">
                    {form.waybillNumber.trim() || <span className="font-sans text-slate-300">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Truck no</dt>
                  <dd className="mt-1 font-mono break-all text-slate-800">
                    {form.truckNumber.trim() || <span className="font-sans text-slate-300">—</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Dispatched by</dt>
                  <dd className="mt-1 break-words text-slate-800">
                    {form.dispatchedBy.trim() || <span className="text-slate-300">—</span>}
                  </dd>
                </div>
              </div>
              <div>
                <dt className="text-xs text-slate-400">Client</dt>
                <dd className="mt-1 break-words text-slate-800">
                  {form.client.trim() || <span className="text-slate-300">—</span>}
                </dd>
              </div>
            </dl>
          </div>
          <DispatchBreakdown breakdown={breakdown} hasLogs={Boolean(form.treeFellId)} />
        </aside>
      </form>
    </div>
  );
};

export default LogTrackingForm;
